import addInteractiveElementsForCompose from 'owa-link-preview/lib/utils/addInteractiveElementsForCompose';
import { BaseLinkPreviewObject } from 'owa-link-preview/lib/utils/BaseLinkPreviewObject';
import { VideoLinkPreviewObject } from 'owa-link-preview/lib/utils/VideoLinkPreviewObject';
import * as Constants from 'owa-link-preview/lib/utils/constants';
import createLinkPreview from 'owa-link-preview/lib/utils/createLinkPreview';
import rehydrateLinkPreview from 'owa-link-preview/lib/utils/rehydrateLinkPreview';
import { PreviewSize, updatePreviewSize } from 'owa-link-preview/lib/utils/updatePreviewSize';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import showVideoPlayer from 'owa-link-preview/lib/components/BingVideoPlayer';
import { isHTMLAnchorElement } from 'owa-cross-window-types';
import { Position } from 'roosterjs-editor-dom';
import {
    ChangeSource,
    EditorPlugin,
    EntityOperation,
    IEditor,
    PluginEvent,
    PluginEventType,
    PositionType,
} from 'roosterjs-editor-types';
import { insertEntity } from 'roosterjs-editor-api';

const EDITOR_WIDTH_COMPACT_IMAGE_SIZE_CUTOFF = 700;
const LINK_PREVIEW_DIV_SELECTOR = '[id^=LPBorder]';
const LINK_PREVIEW_CHANGE_SOURCE = 'LinkPreview';
const LINK_PREVIEW_CLOSE_BUTTON_SELECTOR = `[id^=${Constants.LINK_PREVIEW_CLOSE_BUTTON_CONTAINER_ID}]`;
const LINK_PREVIEW_PLAY_BUTTON_SELECTOR = `[id^=${Constants.LINK_PREVIEW_PLAY_BUTTON_ID}]`;

export default class LinkPreviewPlugin implements EditorPlugin {
    private editor: IEditor;
    private linkPreviewObjects: BaseLinkPreviewObject[];
    private previewSize: PreviewSize;
    private featureEnabled: boolean;

    constructor() {
        this.linkPreviewObjects = [];
        this.featureEnabled = getUserConfiguration().UserOptions.LinkPreviewEnabled;
    }

    public getName() {
        return 'LinkPreview';
    }

    public initialize(editor: IEditor) {
        this.editor = editor;
        lazySubscribeToResizeEvent.importAndExecute(this.onResize);
        this.onResize();
    }

    public dispose() {
        this.linkPreviewObjects = null;
        this.editor = null;
        lazyUnsubscribeFromResizeEvent.importAndExecute(this.onResize);
    }

    public onPluginEvent(event: PluginEvent): void {
        if (!this.featureEnabled) {
            return;
        }

        if (
            event.eventType === PluginEventType.EntityOperation &&
            event.entity.type == Constants.LINK_PREVIEW_ENTITY_TYPE
        ) {
            const { wrapper, id } = event.entity;
            const closeButton = wrapper.querySelector(LINK_PREVIEW_CLOSE_BUTTON_SELECTOR);
            const playButton = wrapper.querySelector(LINK_PREVIEW_PLAY_BUTTON_SELECTOR);
            const linkPreviewDiv = wrapper.querySelector(
                LINK_PREVIEW_DIV_SELECTOR
            ) as HTMLDivElement;

            switch (event.operation) {
                case EntityOperation.NewEntity:
                    if (linkPreviewDiv) {
                        rehydrateLinkPreview(linkPreviewDiv).then(linkPreviewObject => {
                            if (this.editor && linkPreviewObject) {
                                this.linkPreviewObjects.push(linkPreviewObject);
                                addInteractiveElementsForCompose(linkPreviewObject);
                            }
                        });
                    }
                    break;
                case EntityOperation.Click:
                    const target = event.rawEvent?.target as Node;

                    if (closeButton == target || closeButton?.contains(target)) {
                        event.rawEvent?.stopPropagation();
                        const anchor = this.editor.queryElements(
                            '#' + this.getAnchorIdFromEntityId(id)
                        )[0];
                        if (anchor) {
                            anchor.id = Constants.NO_LINK_PREVIEW_ANCHOR_ID + id;
                        }
                        this.onLinkPreviewRemoved(linkPreviewDiv);
                    } else if (playButton == target || playButton?.contains(target)) {
                        event.rawEvent?.stopPropagation();
                        const lp = this.linkPreviewObjects.filter(
                            lp => lp.border == linkPreviewDiv
                        )[0] as VideoLinkPreviewObject;
                        if (lp?.videoPlayer) {
                            showVideoPlayer(lp.videoPlayer);
                        }
                    }
                    break;
                case EntityOperation.RemoveFromStart:
                case EntityOperation.RemoveFromEnd:
                    this.editor.addUndoSnapshot(() => {
                        this.editor.deleteNode(wrapper);
                        if (event.rawEvent) {
                            event.rawEvent.preventDefault();
                            event.rawEvent.stopPropagation();
                        }
                    });

                    break;
                case EntityOperation.ReplaceTemporaryContent:
                    closeButton?.parentNode.removeChild(closeButton);
                    playButton?.parentNode.removeChild(playButton);
                    break;
            }
        } else if (event.eventType === PluginEventType.ContentChanged) {
            if (
                event.source === ChangeSource.AutoLink &&
                event.data &&
                isHTMLAnchorElement(event.data)
            ) {
                const anchor: HTMLAnchorElement = <HTMLAnchorElement>event.data;
                this.generateLinkPreviewBelowAnchor(anchor);
            }
        } else if (event.eventType === PluginEventType.BeforePaste) {
            // VSO 27302: URLs that have already been hyperlinked, i.e. ones copied from mail,
            // should still generate link previews on paste
            const fragment = event.fragment;

            // VSO 27255: Link previews are not generated when hyperlinks are copied and pasted from
            // mail in the editor, but are generated when the message is sent and read in the reading pane
            // Generate a link preview if the fragment contains just the anchor, since it could
            // already contain a link preview, and this covers the above case
            if (fragment?.childNodes && fragment.childNodes.length == 1) {
                const child = fragment.firstElementChild;
                if (isHTMLAnchorElement(child)) {
                    const anchor: HTMLAnchorElement = <HTMLAnchorElement>child;
                    this.generateLinkPreviewBelowAnchor(anchor);
                }
            }
        }
    }

    private onLinkPreviewRemoved(element: HTMLElement) {
        if (this.editor.contains(element)) {
            // Move caret where link preview currently is (below hyperlink)
            this.moveCursorBelowElement(element);

            // Delete link preview and remove from array of link preview objects
            this.editor.deleteNode(element);
            this.linkPreviewObjects = this.linkPreviewObjects.filter(
                (value: BaseLinkPreviewObject) => {
                    return value.border != element;
                }
            );
        }
    }

    private isImageLink(anchor: HTMLAnchorElement): boolean {
        const path: string = anchor.href;
        return !!path && !!path.match('.(bmp|gif|jpe?g|png)$');
    }

    private onImageLoadedIntoEditor(onLoadEvent: Event) {
        // We loaded the image successfully, so unbind event handlers
        const imageElement: HTMLImageElement = <HTMLImageElement>onLoadEvent.target;
        if (imageElement) {
            imageElement.onload = null;
            imageElement.onerror = null;
        }
    }

    private onImageError(onErrorEvent: Event) {
        // We failed to load the image, so unbind event handlers and remove the image element from the message
        const imageElement: HTMLImageElement = <HTMLImageElement>onErrorEvent.target;

        if (imageElement) {
            imageElement.onload = null;
            imageElement.onerror = null;

            if (imageElement.parentNode != null) {
                imageElement.parentNode.removeChild(imageElement);
            }
        }
    }

    private generateLinkPreviewBelowAnchor(anchor: HTMLAnchorElement) {
        if (this.isImageLink(anchor)) {
            this.tryInsertImageIntoEditor(anchor);
        } else {
            createLinkPreview(anchor, true /*inCompose*/).then(linkPreviewObject => {
                if (linkPreviewObject && this.editor) {
                    updatePreviewSize(linkPreviewObject, this.previewSize);
                    this.insertLinkPreviewElementIntoEditor(
                        linkPreviewObject.border,
                        anchor,
                        true /*isReadonly*/
                    );
                }
            });
        }
    }

    private tryInsertImageIntoEditor(anchor: HTMLAnchorElement) {
        // It's possible that the plugin has been disposed by the time this callback is executed, so make sure we still have a
        // valid reference to the editor before continuing
        if (!this.editor) {
            return;
        }

        // Create the image element and try adding it to the editor
        const imageElement: HTMLImageElement = this.editor.getDocument().createElement('img');

        imageElement.onload = this.onImageLoadedIntoEditor;
        imageElement.onerror = this.onImageError;

        imageElement.style.maxWidth = '100%';
        imageElement.src = anchor.href;

        this.insertLinkPreviewElementIntoEditor(imageElement, anchor, false /*isReadonly*/);
    }

    private insertLinkPreviewElementIntoEditor(
        element: HTMLElement,
        anchor: HTMLAnchorElement,
        isReadonly: boolean
    ) {
        this.editor.addUndoSnapshot(() => {
            const entity = insertEntity(
                this.editor,
                Constants.LINK_PREVIEW_ENTITY_TYPE,
                element,
                true /*isBlock*/,
                isReadonly,
                new Position(anchor, PositionType.End)
            );
            anchor.id = this.getAnchorIdFromEntityId(entity.id);
        }, LINK_PREVIEW_CHANGE_SOURCE);
    }

    private getAnchorIdFromEntityId(entityId: string) {
        return Constants.LINK_PREVIEW_ANCHOR_ID + entityId;
    }

    private onResize = () => {
        // resize event can be triggered at the same time when the plugin is disposed due to resizing after we remove event listener,
        // so we need to add a check here
        const scrollContainer = this.editor && this.editor.getScrollContainer();
        if (scrollContainer) {
            if (scrollContainer.clientWidth < EDITOR_WIDTH_COMPACT_IMAGE_SIZE_CUTOFF) {
                this.setPreviewSize(PreviewSize.Compact);
            } else {
                this.setPreviewSize(PreviewSize.Standard);
            }
        }
    };

    private setPreviewSize(newPreviewSize: PreviewSize) {
        if (newPreviewSize != this.previewSize) {
            this.previewSize = newPreviewSize;

            // Update all existing previews:
            if (this.linkPreviewObjects) {
                this.linkPreviewObjects.forEach(lp => {
                    updatePreviewSize(lp, this.previewSize);
                });
            }
        }
    }

    private moveCursorBelowElement(element: HTMLElement) {
        this.editor.select(element, PositionType.After);
    }
}
